// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import React, { useEffect, useMemo, useRef, useState } from "react";

import { mdiDownload } from "@mdi/js";
import { Button, Radio, Select, Space, Table, Tooltip, Typography } from "antd";
import { AxiosError } from "axios";

import IconSpinner from "components/icons/IconSpinner";

import useGeoMapLayers from "../hooks/getGeoMapLayers";
import useGeoMapExport from "../hooks/useGeoMapExport";
import useMapLayerData from "../hooks/useMapLayerData";
import { MapLayer } from "../models/mapLayer";
import styles from "./GeoMapDataTableWidget.module.scss";
import { MapDataKind } from "./GeoMapDataTableWidget.types";

const { Text } = Typography;

const PAGE_SIZE = 100;

function formatValue(val: number | null): string {
  if (val === null || val === undefined) return "—";
  return val.toFixed(4);
}

export function GeoMapDataTableWidget() {
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [selectedLayerIds, setSelectedLayerIds] = useState<number[]>([]);
  const [kind, setKind] = useState<MapDataKind>(1);
  const [page, setPage] = useState(1);
  const [tableScrollY, setTableScrollY] = useState<number | undefined>(undefined);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      const header = el.querySelector<HTMLElement>(".ant-table-thead");
      const pagination = el.querySelector<HTMLElement>(".ant-pagination");
      const headerH = header?.offsetHeight ?? 39;
      const paginationH = pagination ? pagination.offsetHeight + 16 : 56;
      setTableScrollY(Math.max(80, el.clientHeight - headerH - paginationH));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const { data: allLayers = [] } = useGeoMapLayers();

  const { data, isLoading, isError, error } = useMapLayerData(
    selectedLayerIds,
    kind,
    PAGE_SIZE,
    page
  );

  // Build unique group options from all layers
  const groupOptions = useMemo(() => {
    const seen = new Set<string>();
    const opts: { label: string; value: string }[] = [];
    for (const layer of allLayers) {
      if (layer.group && !seen.has(layer.group)) {
        seen.add(layer.group);
        opts.push({ label: layer.group, value: layer.group });
      }
    }
    return opts;
  }, [allLayers]);

  // Build layer options filtered by selected group only
  const layerOptions = useMemo(() => {
    if (!selectedGroup) return [];
    return allLayers
      .filter((layer: MapLayer) => layer.group === selectedGroup)
      .map((layer: MapLayer) => ({
        label: layer.name,
        value: layer._id
      }));
  }, [allLayers, selectedGroup]);

  const handleGroupChange = (value: string) => {
    setSelectedGroup(value);
    setSelectedLayerIds([]);
    setPage(1);
  };

  const handleLayerChange = (values: number[]) => {
    setSelectedLayerIds(values);
    setPage(1);
  };

  const handleKindChange = (value: MapDataKind) => {
    setKind(value);
    setPage(1);
  };

  // Build table columns dynamically
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns = useMemo((): any[] => {
    const numericSorter =
      (key: string) =>
      (a: Record<string, number | null>, b: Record<string, number | null>) => {
        const av = a[key] ?? null;
        const bv = b[key] ?? null;
        if (av === null && bv === null) return 0;
        if (av === null) return 1;
        if (bv === null) return -1;
        return av - bv;
      };

    const stringSorter =
      (key: string) =>
      (a: Record<string, string | null>, b: Record<string, string | null>) =>
        (a[key] ?? "").localeCompare(b[key] ?? "");

    const positionCols =
      kind === 0
        ? [
            {
              title: "Lat",
              dataIndex: "lat",
              key: "lat",
              width: 110,
              render: (val: number | null) => formatValue(val),
              sorter: numericSorter("lat")
            },
            {
              title: "Lng",
              dataIndex: "lng",
              key: "lng",
              width: 110,
              render: (val: number | null) => formatValue(val),
              sorter: numericSorter("lng")
            }
          ]
        : [
            {
              title: "Location",
              dataIndex: "location",
              key: "location",
              width: 160,
              sorter: stringSorter("location")
            }
          ];

    if (!data?.available_parameters?.length) {
      return positionCols;
    }

    const paramCols = data.available_parameters.map((param) => ({
      title: param,
      dataIndex: param,
      key: param,
      width: 130,
      render: (val: number | null) => formatValue(val),
      sorter: numericSorter(param)
    }));

    return [...positionCols, ...paramCols];
  }, [data?.available_parameters, kind]);

  // Flatten data rows so each parameter is a top-level key
  const tableData = useMemo(() => {
    if (!data?.data) return [];
    return data.data.map((item) => ({
      key: item.id ?? `row-${item.location ?? item.lat}`,
      location: item.location ?? null,
      lat: item.lat ?? null,
      lng: item.lng ?? null,
      ...(item.parameters ?? {})
    }));
  }, [data?.data]);

  const errorMessage = useMemo(() => {
    if (!isError) return null;
    const axiosError = error as AxiosError;
    if (axiosError?.response?.status === 404) {
      return kind === 1
        ? "LSD map not yet generated for this layer."
        : "Map not yet generated for this layer.";
    }
    return "Failed to load map data. Please try again.";
  }, [isError, error, kind]);

  const hasSelection = selectedLayerIds.length > 0;

  const { exportCsv, isExporting } = useGeoMapExport();

  return (
    <div className={styles.container}>
      <div className={styles.controls}>
        <div className={styles.selectors}>
          <Select
            placeholder="Select Group"
            options={groupOptions}
            value={selectedGroup}
            onChange={handleGroupChange}
            className={styles.select}
            allowClear
          />
          <Select
            mode="multiple"
            placeholder="Select Layer(s)"
            options={layerOptions}
            value={selectedLayerIds}
            onChange={handleLayerChange}
            disabled={!selectedGroup}
            className={styles.select}
            allowClear
            maxTagCount="responsive"
          />
        </div>
        <Space>
          <Radio.Group
            value={kind}
            onChange={(e) => handleKindChange(e.target.value as MapDataKind)}
            optionType="button"
            buttonStyle="solid">
            <Radio.Button value={1}>LSD Data</Radio.Button>
            <Radio.Button value={0}>Original Data</Radio.Button>
          </Radio.Group>
          <Tooltip title="Export CSV">
            <Button
              onClick={() => exportCsv(selectedLayerIds, kind)}
              loading={isExporting}
              disabled={!hasSelection}
              type="default"
              className={styles.exportButton}
              icon={
                !isExporting && <Icon path={mdiDownload} size={"18px"} color="#a2aaad" />
              }
            />
          </Tooltip>
        </Space>
      </div>

      <div ref={tableContainerRef} className={styles.tableContainer}>
        {isLoading && (
          <div className={styles.feedback}>
            <IconSpinner showBackground />
          </div>
        )}

        {errorMessage && !isLoading && (
          <div className={styles.feedback}>
            <Text type="secondary">{errorMessage}</Text>
          </div>
        )}

        {!isLoading && !errorMessage && hasSelection && data?.total_records === 0 && (
          <div className={styles.feedback}>
            <Text type="secondary">No data available. Generate a map first.</Text>
          </div>
        )}

        {!isLoading && !errorMessage && (
          <Table
            dataSource={tableData}
            columns={columns}
            size="small"
            scroll={{ x: "max-content", y: tableScrollY }}
            pagination={
              hasSelection && data
                ? {
                    current: page,
                    pageSize: PAGE_SIZE,
                    total: data.total_records,
                    showSizeChanger: false,
                    showTotal: (total, range) =>
                      `${range[0]}-${range[1]} of ${total} records`,
                    onChange: (newPage) => setPage(newPage)
                  }
                : false
            }
            locale={{
              emptyText: !hasSelection
                ? "Select a group and layer to view data."
                : undefined
            }}
          />
        )}
      </div>
    </div>
  );
}
